import { zip } from './zip.js';
export { crc32, zip } from './zip.js';
/**
 * The reading half (ADM-03's import). §6 hands the person an .xlsx template
 * and §5 takes "CSV or XLSX" back, so the reader lives beside the writer and
 * `readXlsx(buildXlsx(x))` is a round trip the suite asserts.
 */
export { columnIndex, isoFromSerial, parseCsv, readXlsx, SpreadsheetError, } from './read.js';
export const text = (value) => ({ type: 'text', value });
export const number = (value) => ({ type: 'number', value });
export const date = (value) => ({ type: 'date', value });
/** The five XML escapes, plus the control characters Excel refuses outright. */
function xml(value) {
    return value
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&apos;')
        // XML 1.0 has no way to represent these at all, and Excel rejects the file
        // rather than skipping the cell. A stray U+0007 in a description is
        // otherwise a corrupt-workbook dialog nobody can explain.
        // eslint-disable-next-line no-control-regex -- the point is the control range
        .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '');
}
/** A1, B1 … Z1, AA1 … — the column letters Excel addresses cells by. */
export function cellRef(column, row) {
    let letters = '';
    let n = column;
    do {
        letters = String.fromCharCode(65 + (n % 26)) + letters;
        n = Math.floor(n / 26) - 1;
    } while (n >= 0);
    return `${letters}${row}`;
}
/**
 * Excel's date serial: days since 1899-12-30.
 *
 * NOT 1900-01-01, and the two-day gap is not a typo. Excel deliberately
 * reproduces a Lotus 1-2-3 bug in which 1900 is a leap year, so its day 60 is
 * an imaginary 29 February 1900. Anchoring at 1899-12-30 is the standard way
 * to make every date after 1 March 1900 — which is every date this product
 * will ever export — come out right.
 */
export function dateSerial(iso) {
    const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
    if (!y || !m || !d)
        return 0;
    const utc = Date.UTC(y, m - 1, d);
    const epoch = Date.UTC(1899, 11, 30);
    return Math.round((utc - epoch) / 86_400_000);
}
/** Style indexes into `styles.xml` below. Kept as names, not magic numbers. */
const STYLE_DEFAULT = 0;
const STYLE_HEADER = 1;
const STYLE_DATE = 2;
export function buildXlsx(sheet) {
    const entries = [
        { name: '[Content_Types].xml', content: CONTENT_TYPES },
        { name: '_rels/.rels', content: ROOT_RELS },
        { name: 'xl/workbook.xml', content: workbookXml(sheet.name) },
        { name: 'xl/_rels/workbook.xml.rels', content: WORKBOOK_RELS },
        { name: 'xl/styles.xml', content: STYLES },
        { name: 'xl/worksheets/sheet1.xml', content: sheetXml(sheet) },
    ];
    return zip(entries);
}
function sheetXml(sheet) {
    const header = sheet.columns
        .map((label, index) => `<c r="${cellRef(index, 1)}" s="${STYLE_HEADER}" t="inlineStr"><is><t xml:space="preserve">${xml(label)}</t></is></c>`)
        .join('');
    const body = sheet.rows
        .map((row, rowIndex) => {
        const number_ = rowIndex + 2;
        const cells = row
            .map((cell, columnIndex) => {
            const ref = cellRef(columnIndex, number_);
            switch (cell.type) {
                case 'number': {
                    // The decimal string, unparsed. An empty or non-numeric value
                    // becomes an empty cell rather than a `#VALUE!` — a report with
                    // a blank total is a blank cell, not an error.
                    const value = cell.value.trim();
                    if (value === '' || !/^-?\d+(\.\d+)?$/.test(value)) {
                        return `<c r="${ref}"/>`;
                    }
                    return `<c r="${ref}"><v>${value}</v></c>`;
                }
                case 'date': {
                    const serial = dateSerial(cell.value);
                    if (serial <= 0)
                        return `<c r="${ref}"/>`;
                    return `<c r="${ref}" s="${STYLE_DATE}"><v>${serial}</v></c>`;
                }
                case 'text':
                    if (cell.value === '')
                        return `<c r="${ref}"/>`;
                    return `<c r="${ref}" s="${STYLE_DEFAULT}" t="inlineStr"><is><t xml:space="preserve">${xml(cell.value)}</t></is></c>`;
            }
        })
            .join('');
        return `<row r="${number_}">${cells}</row>`;
    })
        .join('');
    const view = `<sheetView${sheet.rightToLeft ? ' rightToLeft="1"' : ''} workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView>`;
    const dimension = `<dimension ref="A1:${cellRef(Math.max(0, sheet.columns.length - 1), Math.max(1, sheet.rows.length + 1))}"/>`;
    // A generous default width: report columns hold customer names and money,
    // and a sheet that opens with ##### in every total column reads as broken.
    const cols = sheet.columns.length
        ? `<cols><col min="1" max="${sheet.columns.length}" width="18" customWidth="1"/></cols>`
        : '';
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">${dimension}<sheetViews>${view}</sheetViews><sheetFormatPr defaultRowHeight="15"/>${cols}<sheetData><row r="1">${header}</row>${body}</sheetData><autoFilter ref="A1:${cellRef(Math.max(0, sheet.columns.length - 1), 1)}"/></worksheet>`;
}
/** Excel forbids : \ / ? * [ ] in a tab name, and caps it at 31 characters. */
export function safeSheetName(name) {
    const cleaned = name.replace(/[:\\/?*[\]]/g, ' ').trim();
    return (cleaned === '' ? 'Sheet1' : cleaned).slice(0, 31);
}
function workbookXml(name) {
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="${xml(safeSheetName(name))}" sheetId="1" r:id="rId1"/></sheets></workbook>`;
}
const CONTENT_TYPES = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>`;
const ROOT_RELS = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`;
const WORKBOOK_RELS = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`;
/**
 * Three styles, in the order the constants above index them.
 *
 * numFmtId 164 is a custom format — the built-in date formats (14, 22) are
 * LOCALE-DEPENDENT, so an American Excel renders `14` as mm/dd/yy and a
 * British one as dd/mm/yy, which is the ambiguity a document must not have.
 * `yyyy-mm-dd` is unambiguous in every locale and matches what the screen and
 * the PDF show.
 */
const STYLES = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><numFmts count="1"><numFmt numFmtId="164" formatCode="yyyy\\-mm\\-dd"/></numFmts><fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts><fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills><borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="3"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/><xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/></cellXfs><cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles></styleSheet>`;
/** The MIME type an .xlsx download must carry. */
export const XLSX_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
//# sourceMappingURL=index.js.map